import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const TranslatePage: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [romanizedThai, setRomanizedThai] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'toKaraoke' | 'fromKaraoke'>('toKaraoke');
  const [copyButtonText, setCopyButtonText] = useState('Copy');

  useEffect(() => {
    const storedApiKey = localStorage.getItem('REACT_APP_OPENAI_API_KEY');
    if (storedApiKey) {
      setApiKey(storedApiKey);
      console.log('storedApiKey', storedApiKey.length);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newApiKey = e.target.value;
    setApiKey(newApiKey);
    localStorage.setItem('REACT_APP_OPENAI_API_KEY', newApiKey);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputText(text);
      handleTranslate(); // Remove the text parameter
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleTranslate = async () => {
    if (!apiKey) {
      setTranslatedText('Please enter your OpenAI API key first.');
      return;
    }

    let primaryLanguage = localStorage.getItem('primaryLanguage');
    if (!primaryLanguage) {
      setTranslatedText('Please select your primary language first.');
      return;
    }

    setIsLoading(true);

    try {
      const prompt = mode === 'toKaraoke'
        ? `Translate the following text to Thai karaoke (romanized Thai). Provide both the karaoke version and the Thai translation: ${inputText}`
        : `Convert the following Thai karaoke (romanized Thai) text to Thai and then translate it to ${primaryLanguage}. Provide both the Thai version and the ${primaryLanguage} translation: ${inputText}`;

      const response = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: "gpt-4o-mini",
        messages: [
          { role: "system", content: "You are a helpful assistant that specializes in Thai language translation and romanization." },
          { role: "user", content: prompt }
        ]
      }, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      let romanizedThaiContent = '';
      
      if (mode === 'toKaraoke') {
        const romanizedResponse = await axios.post('https://api.openai.com/v1/chat/completions', {
          model: "gpt-4o-mini",
          messages: [
            { role: "system", content: "You are a helpful assistant that extracts romanized Thai from a given text. please only return the romanized Thai text no other text." },
            { role: "user", content: `Extract the romanized Thai from the following text: ${response.data.choices[0].message.content}` }
          ]
        }, {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        });
        
        romanizedThaiContent = romanizedResponse.data.choices[0].message.content;
      }

      setRomanizedThai(romanizedThaiContent);
      setTranslatedText(response.data.choices[0].message.content);
    } catch (error) {
      console.error('Translation error:', error);
      setTranslatedText('An error occurred during translation. Please check your API key and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyRomanized = async () => {
    try {
      await navigator.clipboard.writeText(romanizedThai);
      // update the copy button text
      setCopyButtonText('Copied!');

      // reset the copy button text after 1 second
      setTimeout(() => {
        setCopyButtonText('Copy');
      }, 1000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Thai Karaoke Translator</h1>
      <div style={styles.inputGroup}>
        <label htmlFor="apiKey" style={styles.label}>OpenAI API Key: </label>
        <input
          type="password"
          id="apiKey"
          value={apiKey}
          onChange={handleApiKeyChange}
          placeholder="Enter your OpenAI API key"
          style={styles.input}
        />
      </div>
      <div style={styles.modeSelector}>
        <label>
          <input
            type="radio"
            value="toKaraoke"
            checked={mode === 'toKaraoke'}
            onChange={() => setMode('toKaraoke')}
          />
          To Karaoke
        </label>
        <label>
          <input
            type="radio"
            value="fromKaraoke"
            checked={mode === 'fromKaraoke'}
            onChange={() => setMode('fromKaraoke')}
          />
          From Karaoke
        </label>
      </div>
      <textarea
        value={inputText}
        onChange={handleInputChange}
        placeholder={mode === 'toKaraoke' ? "Enter text to convert to Thai karaoke" : "Enter Thai karaoke text"}
        rows={5}
        cols={50}
        style={styles.textarea}
      />
      <div style={styles.buttonGroup}>
        <button onClick={handlePaste} style={styles.button}>Paste from Clipboard</button>
        <button onClick={handleTranslate} style={styles.button} disabled={isLoading}>
          {isLoading ? 'Translating...' : 'Translate'}
        </button>
      </div>
      <h2 style={styles.subtitle}>Translated Text:</h2>
      {isLoading ? (
        <p style={styles.loadingText}>Translating...</p>
      ) : (
        <>
          <div style={styles.translatedText}>
            <ReactMarkdown>{translatedText}</ReactMarkdown>
          </div>
          {romanizedThai && (
            <div style={styles.romanizedSection}>
              <h3 style={styles.subtitle}>Romanized Thai:</h3>
              <div style={styles.romanizedContainer}>
                <p style={styles.romanizedText}>{romanizedThai}</p>
                <button 
                  onClick={handleCopyRomanized} 
                  style={styles.copyButton}
                >
                  {copyButtonText}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  title: {
    textAlign: 'center' as const,
    color: '#333',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '5px',
  },
  input: {
    width: '100%',
    padding: '8px',
    fontSize: '16px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '8px',
    fontSize: '16px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginBottom: '20px',
    boxSizing: 'border-box' as const,
    minHeight: '100px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexDirection: 'row' as const,
    '@media (max-width: 480px)': {
      flexDirection: 'column' as const,
    },
  },
  button: {
    flex: 1,
    padding: '10px 16px',
    fontSize: '16px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    minWidth: '120px',
    '@media (max-width: 480px)': {
      width: '100%',
    },
  },
  subtitle: {
    color: '#333',
  },
  translatedText: {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    '& p': {  // Add styles for markdown paragraphs
      margin: '0 0 1em 0',
    },
    '& p:last-child': {
      marginBottom: 0,
    }
  },
  loadingText: {
    textAlign: 'center' as const,
    color: '#007bff',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  modeSelector: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-around',
    flexWrap: 'wrap' as const,
    gap: '10px',
  },
  romanizedSection: {
    marginTop: '20px',
  },
  romanizedContainer: {
    display: 'flex',
    alignItems: 'stretch', // 改为 stretch 使子元素高度一致
    gap: '10px',
  },
  romanizedText: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    margin: 0,
  },
  copyButton: {
    padding: '0 12px', // 移除上下内边距
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    minWidth: '80px',
    alignSelf: 'stretch', // 使按钮拉伸以匹配容器高度
    display: 'flex',
    alignItems: 'center', // 垂直居中文本
    justifyContent: 'center', // 水平居中文本
    '@media (max-width: 480px)': {
      padding: '0 16px',
    },
  },
};

export default TranslatePage;
